import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import Sidebar from "../../componentes/Sidebar";
import GradientBanner from "../../componentes/Banner";
import MetaTags from "../../componentes/MetaTags";
import { Link, useNavigate } from "react-router-dom";
import LoadingSkeleton from "../../componentes/LoadingSkeleton";
import { useTranslation } from "react-i18next";
import { fetchAlerts } from "../../api/alerts";
import { formatDate } from "../../utils/dateFormatter";
import {
  faExclamationTriangle,
  faShieldAlt,
  faVirusSlash,
  faBug,
  faRadiation,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { API_BASE_URL } from '../../api/config';
import Pagination from "../../componentes/Pagination";


const baseUrl =  API_BASE_URL;

// Strip HTML tags for card description previews
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

// API fetch functions for news and events
const fetchNews = async (lang) => { // Add lang parameter
  const response = await fetch(`${baseUrl}/api/news?lang=${lang}`); // Include lang in query
  return response.json();
};

const fetchEvents = async (lang) => { // Add lang parameter
  const response = await fetch(`${baseUrl}/api/events?lang=${lang}`); // Include lang in query
  return response.json();
};

const defaultImage = "https://via.placeholder.com/400x200/e5e7eb/9ca3af?text=Alert";

// SeverityButton and getSeverityIcon (unchanged)
const SeverityButton = ({ isActive, onClick, label }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-md font-nunito-sans text-sm font-medium transition-all duration-200 ${
        isActive
          ? "bg-primary text-white shadow-sm"
          : "bg-transparent text-gray-700 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );
};

const getSeverityIcon = (severity) => {
  switch (severity) {
    case "low":
      return <FontAwesomeIcon icon={faShieldAlt} className="text-green-600" />;
    case "medium":
      return <FontAwesomeIcon icon={faBug} className="text-yellow-600" />;
    case "high":
      return (
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-orange-600"
        />
      );
    case "critical":
      return <FontAwesomeIcon icon={faRadiation} className="text-red-600" />;
    default:
      return <FontAwesomeIcon icon={faShieldAlt} className="text-gray-600" />;
  }
};

const Alerts = () => {
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Fetch alerts (main content)
  const { data, isLoading, error } = useQuery({
    queryKey: ["alerts", { severity, dateRange, page, lang }],
    queryFn: () => fetchAlerts({ severity, dateRange, page, lang }),
  });

  // Fetch all alerts for severity counts
  const { data: allAlertsData, isLoading: isLoadingAll } = useQuery({
    queryKey: ["allAlerts", { lang }],
    queryFn: async () => {
      let allResults = [];
      let currentPage = 1;
      let hasNext = true;
      while (hasNext) {
        const response = await fetchAlerts({
          severity: "",
          dateRange: { start: "", end: "" },
          page: currentPage,
          lang,
        });
        allResults = [...allResults, ...response.results];
        hasNext = !!response.next;
        currentPage++;
      }
      return { results: allResults, count: allResults.length };
    },
    staleTime: 1000 * 60 * 60,
  });

  // Fetch news and events for sidebar using useQuery
  const {
    data: newsData,
    isLoading: newsLoading,
  } = useQuery({
    queryKey: ["news", lang], // Add lang to queryKey
    queryFn: () => fetchNews(lang), // Pass lang to fetchNews
  });

  const {
    data: eventsData,
    isLoading: eventsLoading,
  } = useQuery({
    queryKey: ["events", lang], // Add lang to queryKey
    queryFn: () => fetchEvents(lang), // Pass lang to fetchEvents
  });

  const news = newsData?.results || newsData || [];
  const events = eventsData?.results || eventsData || [];
  const loadingSidebar = newsLoading || eventsLoading;

  const alerts = data?.results || [];
  const totalPages = Math.ceil((data?.count || 0) / 5);

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "low":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "critical":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateText = (text, maxLength = 150) => {
    const strippedText = stripHtml(text);
    return strippedText.length <= maxLength
      ? strippedText
      : strippedText.substring(0, maxLength) + "...";
  };

  const handleSeverityChange = (value) => {
    setSeverity(value);
    setPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const calculateSeverityCounts = (alertsData) => {
    const counts = {
      all: alertsData?.count || 0,
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    };
    (alertsData?.results || []).forEach((alert) => {
      if (alert.severity && counts[alert.severity] !== undefined) {
        counts[alert.severity]++;
      }
    });
    return counts;
  };

  const severityCounts = allAlertsData
    ? calculateSeverityCounts(allAlertsData)
    : { all: 0, low: 0, medium: 0, high: 0, critical: 0 };

  if (isLoading || isLoadingAll) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("alerts.title")}
          description={t("alerts.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("alerts.title"), link: null },
          ]}
          backgroundIcons={[
            faExclamationTriangle,
            faShieldAlt,
            faVirusSlash,
            faBug,
            faRadiation,
          ]}
        />
        <FullscreenSpinner />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t("alerts.title")}
          description={t("alerts.subtitle")}
          path={[
            { label: t("header.home"), link: "/" },
            { label: t("alerts.title"), link: null },
          ]}
          backgroundIcons={[
            faExclamationTriangle,
            faShieldAlt,
            faVirusSlash,
            faBug,
            faRadiation,
          ]}
        />
        <div className="flex justify-center items-center min-h-[400px]">
          <p className="text-red-500 font-nunito-sans text-lg">
            {t("alerts.error", { message: error.message })}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <MetaTags title="Alerts — Ethio-CERT" description="Security alerts and advisories from Ethio-CERT." url="https://ethiocert.gov.et/alerts" />
      <PageHeader />
      <GradientBanner
        title={t("alerts.title")}
        description={t("alerts.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("alerts.title"), link: null },
        ]}
        backgroundIcons={[
          faExclamationTriangle,
          faShieldAlt,
          faVirusSlash,
          faBug,
          faRadiation,
        ]}
      />
      <div className="min-h-screen">
        <div className="flex flex-col max-xl:px-4 py-4 md:py-8 px-dynamic">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2 xl:gap-6 mt-6">
            <div className="md:col-span-2">
              <div className="flex flex-col gap-6 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex flex-col w-full sm:w-auto">
                    <label
                      htmlFor="start-date"
                      className="absolute -top-2.5 left-2 bg-white px-1 text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      {t("alerts.filters.start_date")}
                    </label>
                    <input
                      id="start-date"
                      type="date"
                      name="start"
                      onChange={handleFilterChange}
                      value={dateRange.start}
                      max={dateRange.end || undefined}
                      className="w-full border-2 border-primary/20 rounded-md pt-3 pb-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito-sans text-sm"
                    />
                  </div>
                  <div className="relative flex flex-col w-full sm:w-auto">
                    <label
                      htmlFor="end-date"
                      className="absolute -top-2.5 left-2 bg-white px-1 text-xs font-medium text-gray-600 uppercase tracking-wider"
                    >
                      {t("alerts.filters.end_date")}
                    </label>
                    <input
                      id="end-date"
                      type="date"
                      name="end"
                      onChange={handleFilterChange}
                      value={dateRange.end}
                      min={dateRange.start || undefined}
                      className="w-full border-2 border-primary/20 rounded-md pt-3 pb-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-nunito-sans text-sm"
                    />
                  </div>
                </div>
                <div className="bg-gray-100 rounded-lg p-1 flex flex-wrap gap-1">
                  <SeverityButton
                    isActive={severity === ""}
                    onClick={() => handleSeverityChange("")}
                    label={t("alerts.severities.all")}
                  />
                  <SeverityButton
                    isActive={severity === "low"}
                    onClick={() => handleSeverityChange("low")}
                    label={t("alerts.severities.low")}
                  />
                  <SeverityButton
                    isActive={severity === "medium"}
                    onClick={() => handleSeverityChange("medium")}
                    label={t("alerts.severities.medium")}
                  />
                  <SeverityButton
                    isActive={severity === "high"}
                    onClick={() => handleSeverityChange("high")}
                    label={t("alerts.severities.high")}
                  />
                  <SeverityButton
                    isActive={severity === "critical"}
                    onClick={() => handleSeverityChange("critical")}
                    label={t("alerts.severities.critical")}
                  />
                </div>
              </div>
              <div className="space-y-6">
                {alerts.length > 0 ? (
                  alerts.map((alert) => {
                    const hasImage = alert.image;
                    
                    return (
                      <Link
                        key={alert.id}
                        to={`/alerts/${alert.slug}`}
                        className="block group hover:no-underline"
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="bg-white rounded-xl border border-gray-200 hover:border-primary/30 hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                        <div className={`flex ${hasImage ? 'max-sm:flex-col' : 'flex-col'}`}>
                          {hasImage && (
                            <div className="w-2/5 aspect-[16/10] max-sm:w-full flex-shrink-0 relative overflow-hidden">
                              <img
                                src={alert.image}
                                alt={alert.title || "Alert"}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.src = defaultImage;
                                }}
                              />
                              <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-nunito-sans font-semibold ${getSeverityClass(alert.severity)}`}>
                                {getSeverityIcon(alert.severity)}
                                <span className="ml-2">
                                  {t(`alerts.severities.${alert.severity}`) || "Unknown"}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div className={`flex-1 min-w-0 p-6 ${!hasImage ? 'border-l-4 border-primary/20' : ''}`}>
                            {!hasImage && (
                              <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-nunito-sans font-semibold mb-3 ${getSeverityClass(alert.severity)}`}>
                                {getSeverityIcon(alert.severity)}
                                <span className="ml-2">
                                  {t(`alerts.severities.${alert.severity}`) || "Unknown"}
                                </span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-3 mb-3">
                              <span className="flex items-center text-sm text-gray-500 font-nunito-sans">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 mr-2 text-primary" />
                                {formatDate(alert.created_at, lang)}
                              </span>
                            </div>
                            
                            <h2 className="font-catamaran text-xl font-bold text-gray-900 mb-3 line-clamp-2 break-words group-hover:text-primary transition-colors duration-300">
                              {alert.title || "No Title"}
                            </h2>
                            
                            <p className="font-nunito-sans text-gray-600 text-sm line-clamp-3 break-words leading-relaxed">
                              {truncateText(alert.description || "No Description")}
                            </p>
                            
                            <div className="mt-4 flex items-center justify-between">
                              <span className="text-primary font-nunito-sans text-sm font-medium group-hover:underline">
                                {t("alerts.read_more")}
                              </span>
                              {!hasImage && (
                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-2xl" />
                              )}
                            </div>
                          </div>
                        </div>
                        </motion.div>
                      </Link>
                    );
                  })
                ) : (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16"
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-gray-300 text-6xl mb-4" />
                    <h3 className="font-catamaran text-xl font-bold text-gray-700 mb-2">
                      No Alerts Found
                    </h3>
                    <p className="font-nunito-sans text-gray-500">
                      {t("alerts.no_results")}
                    </p>
                  </motion.div>
                )}
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                previousLabel={t("common.previous")}
                nextLabel={t("common.next")}
              />
            </div>
            <Sidebar
              primaryItems={news}
              secondaryItems={events}
              counts={severityCounts}
              loading={loadingSidebar}
              primaryTitle="news"
              secondaryTitle="events"
              countTitle="alerts.title"
              getCountIcon={getSeverityIcon}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Alerts;